// ============================================================
// detailTypes.js — the four capital types, their words and their colour.
//
// There is no Loan / RealEstate / Investment module: a loan is a DealDetail
// with DetailType = 'N/R', a property 'R/E', and a position 'Active' or
// 'Passive'. Those four strings are the only capital-type vocabulary the
// platform has, and the deal page has always badged a capital item with them.
//
// WHY THIS IS A MODULE. The list and its colours lived as two consts inside
// pages/Deals.jsx. The Cash Flow Projection report now badges the same four
// types on its own rows, and a second copy of a colour map is a pair that
// drifts — the two screens would badge one capital item two different colours
// and neither would say why. mobile/src/components/DealDetailView.tsx carries a
// third copy with a comment saying it "matches" this one; it cannot import from
// frontend/, so it stays a mirrored pair and stays commented as such.
//
// `colorOf` / `labelOf` FALL BACK RATHER THAN THROWING. DealDetails.DetailType
// is NVARCHAR with no CHECK constraint, so a value from an older row or a
// hand-edited record is possible — and a badge is cosmetic. An unknown type
// renders in navy under its own raw string, which is more useful than a blank.
// ============================================================
import { B } from './brand';

export const DETAIL_TYPES = ['N/R', 'R/E', 'Active', 'Passive'];

export const DETAIL_TYPE_OPTIONS = [
  { value: 'N/R', label: 'Note/Loan (N/R)' },
  { value: 'R/E', label: 'Real Estate (R/E)' },
  { value: 'Active', label: 'Active Investment' },
  { value: 'Passive', label: 'Passive Investment' },
];

// A DEAL-LEVEL SAGE ENTITY IS NOT A CAPITAL TYPE, and it is deliberately not in
// DETAIL_TYPES: it is the holding company attached to the DEAL
// (dbo.DealSageEntities), which the Cash Flow Projection report renders as a
// row of its own beside the capital items — the LLC's postings and borrowing,
// typed 'Entity' by the API. It gets a colour and a label here so the one
// badge component paints it apart from the four types, and so no page has a
// second colour map for it.
export const ENTITY_TYPE = 'Entity';

export const DETAIL_TYPE_COLOR = {
  'N/R': B.navy,
  'R/E': B.tealDk,
  Active: B.green,
  Passive: B.amber,
  // Muted on purpose — the four types are the categorical palette, and an
  // entity is "the deal's own holding company", not a fifth kind of capital.
  [ENTITY_TYPE]: B.textMd,
};

// The short form, for a badge in a dense row: the code itself is what people
// say out loud ("it's an N/R"), and the long label is what a form needs.
export const DETAIL_TYPE_SHORT = {
  'N/R': 'N/R',
  'R/E': 'R/E',
  Active: 'Active',
  Passive: 'Passive',
  [ENTITY_TYPE]: 'Entity',
};

export const colorOf = (type) => DETAIL_TYPE_COLOR[type] || B.navy;

export const labelOf = (type) =>
  (type === ENTITY_TYPE ? 'Sage entity (deal level)' : null)
  || (DETAIL_TYPE_OPTIONS.find((o) => o.value === type) || {}).label || type || 'Capital item';

export const shortOf = (type) => DETAIL_TYPE_SHORT[type] || type || 'Capital item';
